import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import authService from '../services/authService';
import Logo from '../components/common/Logo';
import { useAnalytics } from '../hooks/useAnalytics';

interface AuthPageProps {
  onLogin: () => Promise<void> | void;
}

export default function AuthPage({ onLogin }: AuthPageProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  const getGoogleClientId = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      throw new Error('VITE_GOOGLE_CLIENT_ID is not set');
    }
    return clientId;
  };

  const GOOGLE_CLIENT_ID = getGoogleClientId();
  const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID;

  useEffect(() => {
    const checkSdksLoaded = () => {
      const googleLoaded = typeof window !== 'undefined' && window.google;
      const appleLoaded = typeof window !== 'undefined' && window.AppleID;
      
      if (googleLoaded) {
        setIsGoogleLoaded(true);
        authService.initializeGoogleAuth(GOOGLE_CLIENT_ID);
      }
      
      if (appleLoaded && APPLE_CLIENT_ID) {
        authService.initializeAppleAuth(APPLE_CLIENT_ID);
      }
      
      if (!googleLoaded || !appleLoaded) {
        setTimeout(checkSdksLoaded, 100);
      }
    };
    checkSdksLoaded();
  }, []);

  const handleGoogleSignIn = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔘 User clicked "Continue with Google" button');
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.googleSignIn();
      
      if (response.success) {
        console.log('🎉 Authentication successful!');
        
        // Track authentication event
        const isNewUser = response.isNewUser || false;
        trackEvent(isNewUser ? 'webplay.user_signed_up' : 'webplay.user_logged_in', {
          auth_provider: 'google',
          is_new_user: isNewUser,
          source: 'web',
          login_method: 'oauth'
        });
        
        await onLogin();
        navigate('/');
      } else {
        console.error('❌ Authentication failed:', response.message);
        setError(response.message || 'Authentication failed');
        
        // Track authentication failure
        trackEvent('webplay.auth_failed', {
          auth_provider: 'google',
          error_type: 'authentication_failed',
          error_message: response.message || 'Authentication failed'
        });
      }
    } catch (err: any) {
      console.error('💥 Google sign-in error:', err);
      setError(err?.message || 'Failed to sign in with Google');
      
      // Track authentication error
      trackEvent('webplay.auth_failed', {
        auth_provider: 'google',
        error_type: 'exception',
        error_message: err?.message || 'Failed to sign in with Google'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 dark:from-dark-bg dark:to-dark-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo to="/" size="lg" showIcon={true} />
          </div>
          <p className="text-lg text-muted">
            Discover and play interactive livestream games
          </p>
        </div>

        {/* Auth Card */}
        <div className="card p-8">
          <h2 className="text-2xl font-semibold text-center mb-6 text-gray-900 dark:text-white">
            Welcome to Beemi Web
          </h2>
          
          <p className="text-center text-muted mb-6">
            Sign in to start playing games
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading || !isGoogleLoaded}
            className="w-full bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-6 py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mb-3 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
          >
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF00CF' }} />
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            <span className="font-semibold">{isLoading ? 'Signing in...' : 'Continue with Google'}</span>
          </button>

          <p className="text-xs text-center text-muted mt-6">
            By continuing, you agree to Beemi's Terms of Service and Privacy Policy
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-muted">
            Beemi Web - Interactive Gaming Platform
          </p>
        </div>
      </div>
    </div>
  );
}

